import React, { useState, useEffect } from 'react'
import { useAuthStore } from '../store/useAuthStore'

const TimeTrackingPage = () => {
  const { profile } = useAuthStore()
  const [isTracking, setIsTracking] = useState(false)
  const [currentTask, setCurrentTask] = useState('')
  const [startTime, setStartTime] = useState(null)
  const [elapsedTime, setElapsedTime] = useState(0)
  const [timesheets, setTimesheets] = useState([])
  const [selectedProject, setSelectedProject] = useState('')

  // Mock projects data
  const projects = [
    { id: 1, name: "E-commerce Website", client: "John Doe" },
    { id: 2, name: "Mobile App Development", client: "Jane Smith" },
    { id: 3, name: "Digital Marketing", client: "Mike Johnson" }
  ]

  // Mock timesheets data
  useEffect(() => {
    const mockTimesheets = [
      {
        id: 1,
        project: "E-commerce Website",
        task: "Design user interface",
        date: "2024-01-20",
        startTime: "09:00",
        endTime: "12:00",
        hours: 3,
        note: "Created wireframes and mockups"
      },
      {
        id: 2,
        project: "E-commerce Website",
        task: "Database setup",
        date: "2024-01-21",
        startTime: "10:00",
        endTime: "15:00",
        hours: 5,
        note: "Configured PostgreSQL database"
      },
      {
        id: 3,
        project: "Mobile App Development",
        task: "API integration",
        date: "2024-01-22",
        startTime: "14:00",
        endTime: "17:00",
        hours: 3,
        note: "Integrated payment gateway"
      }
    ]
    setTimesheets(mockTimesheets)
  }, [])

  // Timer effect
  useEffect(() => {
    let interval = null
    if (isTracking && startTime) {
      interval = setInterval(() => {
        setElapsedTime(Date.now() - startTime)
      }, 1000)
    } else if (!isTracking) {
      clearInterval(interval)
    }
    return () => clearInterval(interval)
  }, [isTracking, startTime])

  const formatTime = (milliseconds) => {
    const totalSeconds = Math.floor(milliseconds / 1000)
    const hours = Math.floor(totalSeconds / 3600)
    const minutes = Math.floor((totalSeconds % 3600) / 60)
    const seconds = totalSeconds % 60
    return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`
  }

  const startTracking = () => {
    if (!currentTask || !selectedProject) {
      alert('Please select a project and enter a task description')
      return
    }
    setIsTracking(true)
    setStartTime(Date.now())
    setElapsedTime(0)
  }

  const stopTracking = () => {
    setIsTracking(false)
    // Here you would save the timesheet entry to the database
    const newEntry = {
      id: timesheets.length + 1,
      project: projects.find(p => p.id === parseInt(selectedProject))?.name || '',
      task: currentTask,
      date: new Date().toISOString().split('T')[0],
      startTime: new Date(startTime).toLocaleTimeString('en-US', { hour12: false }).slice(0, 5),
      endTime: new Date().toLocaleTimeString('en-US', { hour12: false }).slice(0, 5),
      hours: Math.round((elapsedTime / (1000 * 60 * 60)) * 100) / 100,
      note: ''
    }
    setTimesheets(prev => [newEntry, ...prev])
    setCurrentTask('')
    setSelectedProject('')
  }

  const getTotalHours = () => {
    return timesheets.reduce((total, entry) => total + entry.hours, 0)
  }

  const getTodayHours = () => {
    const today = new Date().toISOString().split('T')[0]
    return timesheets
      .filter(entry => entry.date === today)
      .reduce((total, entry) => total + entry.hours, 0)
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b border-gray-200">
        <div className="page-container py-6">
          <h1 className="text-3xl font-bold text-gray-900">Time Tracking</h1>
          <p className="text-gray-600 mt-2">Track your work hours and manage timesheets</p>
        </div>
      </div>

      <div className="page-container py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Time Tracker */}
          <div className="lg:col-span-1">
            <div className="card">
              <h2 className="text-xl font-semibold mb-6">Time Tracker</h2>
              
              {/* Timer Display */}
              <div className="text-center mb-6">
                <div className="text-4xl font-mono font-bold text-primary-600 mb-2">
                  {formatTime(elapsedTime)}
                </div>
                <div className={`w-4 h-4 rounded-full mx-auto ${isTracking ? 'bg-green-500' : 'bg-gray-400'}`}></div>
                <p className="text-sm text-gray-600 mt-2">
                  {isTracking ? 'Tracking...' : 'Stopped'}
                </p>
              </div>

              {/* Project Selection */}
              <div className="mb-4">
                <label className="label">Project</label>
                <select
                  value={selectedProject}
                  onChange={(e) => setSelectedProject(e.target.value)}
                  className="input-field"
                  disabled={isTracking}
                >
                  <option value="">Select a project</option>
                  {projects.map(project => (
                    <option key={project.id} value={project.id}>
                      {project.name} - {project.client}
                    </option>
                  ))}
                </select>
              </div>

              {/* Task Description */}
              <div className="mb-6">
                <label className="label">Task Description</label>
                <input
                  type="text"
                  value={currentTask}
                  onChange={(e) => setCurrentTask(e.target.value)}
                  className="input-field"
                  placeholder="What are you working on?"
                  disabled={isTracking}
                />
              </div>

              {/* Control Buttons */}
              <div className="space-y-3">
                {!isTracking ? (
                  <button
                    onClick={startTracking}
                    className="btn-primary w-full"
                    disabled={!currentTask || !selectedProject}
                  >
                    Start Tracking
                  </button>
                ) : (
                  <button
                    onClick={stopTracking}
                    className="btn-secondary w-full bg-red-600 hover:bg-red-700 text-white"
                  >
                    Stop Tracking
                  </button>
                )}
              </div>
            </div>

            {/* Stats */}
            <div className="card mt-6">
              <h3 className="text-lg font-semibold mb-4">Today's Summary</h3>
              <div className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-gray-600">Today's Hours:</span>
                  <span className="font-semibold">{getTodayHours().toFixed(2)}h</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Total Hours:</span>
                  <span className="font-semibold">{getTotalHours().toFixed(2)}h</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Entries:</span>
                  <span className="font-semibold">{timesheets.length}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Timesheets */}
          <div className="lg:col-span-2">
            <div className="card">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-xl font-semibold">Timesheets</h2>
                <button className="btn-primary">Export Timesheet</button>
              </div>

              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-gray-200">
                      <th className="text-left py-3 px-4 font-medium text-gray-600">Date</th>
                      <th className="text-left py-3 px-4 font-medium text-gray-600">Project</th>
                      <th className="text-left py-3 px-4 font-medium text-gray-600">Task</th>
                      <th className="text-left py-3 px-4 font-medium text-gray-600">Time</th>
                      <th className="text-left py-3 px-4 font-medium text-gray-600">Hours</th>
                      <th className="text-left py-3 px-4 font-medium text-gray-600">Note</th>
                    </tr>
                  </thead>
                  <tbody>
                    {timesheets.map((entry) => (
                      <tr key={entry.id} className="border-b border-gray-100 hover:bg-gray-50">
                        <td className="py-3 px-4 text-sm">{entry.date}</td>
                        <td className="py-3 px-4 text-sm font-medium">{entry.project}</td>
                        <td className="py-3 px-4 text-sm">{entry.task}</td>
                        <td className="py-3 px-4 text-sm text-gray-600">
                          {entry.startTime} - {entry.endTime}
                        </td>
                        <td className="py-3 px-4 text-sm font-semibold">{entry.hours}h</td>
                        <td className="py-3 px-4 text-sm text-gray-600">{entry.note}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {timesheets.length === 0 && (
                <div className="text-center py-8 text-gray-500">
                  No timesheet entries yet. Start tracking your time!
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default TimeTrackingPage
